import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Activity, Clock, Crown, Flag, ImageOff, Lock, MessageCircle, Mountain, Pencil, Share2, StickyNote, Timer, Trophy } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { useSpeedUnit } from '../../contexts/SpeedUnitContext';
import { avatarPresetIcon } from '../../lib/avatarPresets';
import { springs } from '../../lib/motion';
import { cn, distanceToDisplay, formatSpeedByUnit, getDistanceLabel } from '../../lib/utils';
import { mediaUrl, requestSessionAccess } from '../../api/social';
import { PostMap } from './RouteMap';

// ---------------------------------------------------------------------------
// Formatters / post helpers (shared across the Social feature)
// ---------------------------------------------------------------------------

export const formatLapTime = (seconds) => {
  if (seconds == null || !isFinite(seconds)) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(3).padStart(6, '0');
  return `${mins}:${secs}`;
};

/** Duration as 25:45 or 1:07:00 (iOS time format). */
export const formatDuration = (seconds) => {
  if (seconds == null || !isFinite(seconds)) return '—';
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
};

// Backend timestamps are UTC but zone-less; anchor them so viewers in any
// timezone read the right wall-clock time.
const parseUtc = (iso) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

/** "Today" / "Yesterday" / "Jun 20" */
export const formatPostDate = (iso) => {
  if (!iso) return '';
  const d = parseUtc(iso);
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Normalize auto-generated titles like "Sebring — 20/03/2026" to app-wide
 * "Sebring — Mar 20, 2026". Leave custom user titles untouched.
 */
export const formatPostTitle = (title) => {
  if (!title || typeof title !== 'string') return title || '';
  const m = title.match(
    /^(.+?)\s*[—–-]\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\s*$/
  );
  if (!m) return title;
  const [, track, a, b, yRaw] = m;
  const year = yRaw.length === 2 ? 2000 + Number(yRaw) : Number(yRaw);
  // Prefer DMY (common in stored titles like 20/03/2026); if month>12 swap to MDY
  let day = Number(a);
  let month = Number(b);
  if (month > 12 && day <= 12) {
    day = Number(b);
    month = Number(a);
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return title;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return title;
  const nice = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${track.trim()} — ${nice}`;
};

/** Short relative time for comments: "3 hr ago". */
export const formatRelativeShort = (iso) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - parseUtc(iso).getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return formatPostDate(iso);
};

export const postLocation = (post) =>
  post.stats?.location || post.session?.track_name || null;

/** "Jun 20 · Angeles Crest" subtitle; falls back to "Laptura". */
export const postSubtitle = (post) => {
  const parts = [formatPostDate(post.created_at), postLocation(post)].filter(Boolean);
  return parts.join(' · ') || 'Laptura';
};

/** Normalized stats: post.stats first, session summary as fallback. */
export const postStats = (post) => ({
  bestLapS: post.stats?.best_lap_s ?? post.session?.best_lap_time ?? null,
  distanceKm: post.stats?.distance_km ?? null,
  timeS: post.stats?.moving_time_s ?? post.session?.session_duration ?? null,
  topSpeedKmh: post.stats?.top_speed_kmh ?? post.session?.top_speed ?? null,
});

/** Canyon cards use Distance/Time/Top speed. Session/track/lap always keep Best lap. */
export const statRowLabels = (kind) =>
  kind === 'canyon' ? ['Distance', 'Time', 'Top speed'] : ['Best lap', 'Distance', 'Top speed'];

export async function sharePost(post) {
  const { distanceKm } = postStats(post);
  const text = [post.title, distanceKm != null ? `${distanceKm.toFixed(1)} km` : null, 'on Laptura']
    .filter(Boolean)
    .join(' — ');
  try {
    if (navigator.share) {
      await navigator.share({ title: post.title, text });
    } else {
      await navigator.clipboard.writeText(text);
      toast('Copied to clipboard');
    }
  } catch {
    // user cancelled the share sheet — nothing to do
  }
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/**
 * Avatar circle: photo, built-in icon preset (preset:{id}), or initials.
 */
export function InitialsAvatar({ name, avatarUrl, userId, isMe, size = 38, onClick, className, ariaLabel, testId }) {
  const [failedUrl, setFailedUrl] = useState(null);
  // Feed/comment payloads snapshot author.avatar_url when they are fetched, so
  // they go stale the moment you change your own photo (the cards kept the old
  // one until a page refresh). When this circle is that user, render live auth
  // state. `isMe` is only paint — Register uses it for the self-style chip and
  // a local blob/preset for the src. Treating isMe as "always use the session
  // avatar" made remove and icon swaps no-ops whenever a session existed.
  // useAuth() is undefined outside AuthProvider (tests, storybook) — tolerate it.
  const me = (useAuth() || {}).user;
  const isSelf = Boolean(me) && userId != null && userId === me.id;
  const resolvedUrl = isSelf ? me.avatar_url : avatarUrl;
  const resolvedName = isSelf ? me.name || me.username || name : name;

  const initials = (resolvedName || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  const PresetIcon = avatarPresetIcon(resolvedUrl);
  const isPreset = typeof resolvedUrl === 'string' && resolvedUrl.startsWith('preset:');
  const showPhoto = resolvedUrl && !isPreset && resolvedUrl !== failedUrl;
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-label={onClick ? ariaLabel || `View ${name || 'user'}’s profile` : undefined}
      data-testid={testId}
      className={cn(
        'rounded-full overflow-hidden flex items-center justify-center font-extrabold shrink-0 leading-none',
        isMe ? 'bg-lap-raised text-lap-accent-bright' : 'bg-lap-accent text-white',
        className
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
    >
      {showPhoto ? (
        <img
          src={mediaUrl(resolvedUrl)}
          alt=""
          className="w-full h-full object-cover"
          onError={() => setFailedUrl(resolvedUrl)}
        />
      ) : PresetIcon ? (
        <PresetIcon
          className="shrink-0"
          style={{ width: Math.round(size * 0.5), height: Math.round(size * 0.5) }}
          strokeWidth={2}
        />
      ) : (
        initials
      )}
    </Tag>
  );
}

// ---------------------------------------------------------------------------
// Media carousel
// ---------------------------------------------------------------------------

/** Keyed on src so a swapped photo gets fresh state instead of a stale flag. */
function MediaImage({ src }) {
  return <MediaImageFrame key={src} src={src} />;
}

function MediaImageFrame({ src }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'

  // A cached image can finish decoding before React attaches onLoad, so the
  // handler never fires and the skeleton would spin forever (exactly what
  // happened on revisiting a page whose photos were already warm). Ask the
  // element itself the moment we get it, and trust `complete` over the event.
  const probe = useCallback((node) => {
    if (!node || !node.complete) return;
    setStatus(node.naturalWidth > 0 ? 'loaded' : 'error');
  }, []);

  if (status === 'error') {
    // Without this branch a blocked/deleted image left the pulse skeleton
    // spinning forever, which reads as "the upload never finished".
    return (
      <div
        className="absolute inset-0 bg-lap-card flex flex-col items-center justify-center gap-1.5 text-lap-dim"
        data-testid="media-image-error"
      >
        <ImageOff className="w-5 h-5" />
        <span className="text-xs font-medium">Photo unavailable</span>
      </div>
    );
  }
  return (
    <>
      {status !== 'loaded' && <div className="absolute inset-0 bg-lap-raised animate-pulse" aria-hidden />}
      <img
        ref={probe}
        src={src}
        alt=""
        loading="lazy"
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
        className={cn(
          'w-full h-full object-cover transition-opacity duration-200',
          status === 'loaded' ? 'opacity-100' : 'opacity-0'
        )}
      />
    </>
  );
}

/**
 * Swipeable scroll-snap carousel for post media (first item = cover), with
 * the route map appended as the last slide when the post has one — Strava
 * style. Only rendered when the post actually has media; media-less posts
 * keep using PostMap directly.
 */
export function MediaCarousel({ post, className }) {
  const scrollerRef = useRef(null);
  const [index, setIndex] = useState(0);
  const media = post.media || [];
  const hasMap = post.route?.length >= 2 || post.session?.session_id;
  const count = media.length + (hasMap ? 1 : 0);

  const goTo = (i) =>
    scrollerRef.current?.scrollTo({ left: i * scrollerRef.current.clientWidth, behavior: 'smooth' });

  return (
    <div className={cn('relative overflow-hidden bg-lap-card', className)} data-testid="media-carousel">
      <div
        ref={scrollerRef}
        onScroll={(e) =>
          setIndex(Math.round(e.currentTarget.scrollLeft / (e.currentTarget.clientWidth || 1)))
        }
        className="flex h-full w-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
      >
        {media.map((m) => (
          <div key={m.id} className="relative w-full h-full shrink-0 snap-center bg-black/40">
            {m.kind === 'video' ? (
              <video
                src={mediaUrl(m.url)}
                controls
                preload="metadata"
                playsInline
                className="w-full h-full"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <MediaImage src={mediaUrl(m.url)} />
            )}
          </div>
        ))}
        {hasMap && (
          <div className="w-full h-full shrink-0 snap-center">
            <PostMap post={post} className="w-full h-full" />
          </div>
        )}
      </div>
      {count > 1 && (
        <div
          className="absolute bottom-2 left-1/2 -translate-x-1/2 z-[710] flex items-center gap-1.5 px-2 py-1.5 rounded-full bg-black/45"
          data-testid="carousel-dots"
        >
          {Array.from({ length: count }, (_, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                goTo(i);
              }}
              className={cn(
                'w-1.5 h-1.5 rounded-full transition-colors',
                i === index ? 'bg-white' : 'bg-white/40'
              )}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const KINDS = {
  track: { label: 'Track day', icon: Flag },
  canyon: { label: 'Canyon', icon: Mountain },
  session: { label: 'Session', icon: Activity },
  lap: { label: 'Lap', icon: Timer },
  note: { label: 'Note', icon: StickyNote },
};

/** Kind capsule — accent on cards, neutral on the detail page. */
export function KindBadge({ kind, variant = 'accent' }) {
  const k = KINDS[kind] || KINDS.note;
  const Icon = k.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wider shrink-0',
        variant === 'neutral'
          ? 'bg-white/[0.07] border-white/10 text-lap-muted'
          : 'bg-lap-accent/[0.14] border-lap-accent/40 text-lap-accent-bright'
      )}
    >
      <Icon className="w-3 h-3" />
      {k.label}
    </span>
  );
}

const BURST_SPARKLES = [
  { a: 0, r: 15, kind: 'dot', size: 3, color: '#FFE9A0', delay: 0 },
  { a: 40, r: 20, kind: 'diamond', size: 4, color: '#E8C04A', delay: 0.03 },
  { a: 80, r: 14, kind: 'dot', size: 2, color: '#F5D76E', delay: 0.05 },
  { a: 120, r: 19, kind: 'diamond', size: 3.5, color: '#FFE9A0', delay: 0.02 },
  { a: 160, r: 16, kind: 'dot', size: 2.5, color: '#E8C04A', delay: 0.04 },
  { a: 200, r: 21, kind: 'diamond', size: 4, color: '#F5D76E', delay: 0.01 },
  { a: 240, r: 13, kind: 'dot', size: 2, color: '#FFE9A0', delay: 0.06 },
  { a: 280, r: 18, kind: 'diamond', size: 3, color: '#E8C04A', delay: 0.03 },
  { a: 320, r: 17, kind: 'dot', size: 3, color: '#F5D76E', delay: 0.02 },
];

/** Gold trophy Respect — warm gold + soft glow + sparkle burst (not theme accent). */
export function RespectButton({ liked, count, onToggle, className }) {
  const reduce = useReducedMotion();
  const [burst, setBurst] = useState(0);

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (!liked && !reduce) setBurst((k) => k + 1);
        onToggle();
      }}
      className={cn(
        'flex items-center gap-1.5 p-2.5 -m-1.5 rounded-full transition-colors active:scale-[0.94]',
        liked ? 'text-[#E8C04A]' : 'text-lap-dim hover:text-[#E8C04A]/80',
        className
      )}
      aria-label={liked ? 'Remove respect' : 'Give respect'}
      title="Respect"
      data-testid="respect-button"
    >
      <motion.span
        key={`pop-${liked}`}
        className="relative"
        initial={{ scale: reduce ? 1 : 1.35 }}
        animate={{ scale: 1 }}
        transition={springs.snappy}
      >
        <Trophy
          className={cn(
            'w-[18px] h-[18px]',
            liked && 'fill-[#E8C04A] [filter:drop-shadow(0_0_5px_rgba(232,192,74,0.85))]'
          )}
          strokeWidth={liked ? 1.75 : 2}
        />
        {burst > 0 && (
          <span key={burst} className="absolute left-1/2 top-1/2 pointer-events-none" aria-hidden>
            {BURST_SPARKLES.map((s) => (
              <motion.span
                key={`${s.a}-${s.kind}`}
                className="absolute"
                style={{
                  width: s.size,
                  height: s.size,
                  marginLeft: -s.size / 2,
                  marginTop: -s.size / 2,
                  backgroundColor: s.color,
                  borderRadius: s.kind === 'dot' ? 999 : 1,
                  boxShadow: `0 0 4px ${s.color}`,
                  transform: s.kind === 'diamond' ? 'rotate(45deg)' : undefined,
                }}
                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                animate={{
                  x: Math.cos((s.a * Math.PI) / 180) * s.r,
                  y: Math.sin((s.a * Math.PI) / 180) * s.r,
                  opacity: 0,
                  scale: 0.25,
                }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: s.delay }}
              />
            ))}
          </span>
        )}
      </motion.span>
      <span className={cn('num text-[13px] font-medium', count === 0 && 'text-lap-faint')}>{count || ''}</span>
    </button>
  );
}

/**
 * Session data access pill for another user's session post. Reflects
 * post.session_access from the backend: request → optimistic 'pending',
 * approved → jump straight into Analyze, denied → subdued re-request.
 * Compact variant lives on the feed card action row; full on the detail page.
 */
export function SessionAccessPill({ post, compact = false, onChange }) {
  const navigate = useNavigate();
  const propStatus = post.session_access?.status;
  const [status, setStatus] = useState(propStatus);
  useEffect(() => setStatus(propStatus), [propStatus]);

  if (!post.session?.session_id || post.is_mine || !status) return null;

  const set = (s) => {
    setStatus(s);
    onChange?.({ status: s });
  };

  const openAnalyze = (e) => {
    e.stopPropagation();
    localStorage.setItem('selectedSessionId', String(post.session.session_id));
    navigate('/Laptura/session-analysis');
  };

  const request = async (e) => {
    e.stopPropagation();
    const prev = status;
    set('pending');
    try {
      await requestSessionAccess(post.session.session_id);
    } catch (err) {
      // ponytail: 409 = a request already exists server-side; the optimistic
      // 'pending' is close enough until the next post fetch reconciles.
      if (err?.response?.status === 409) return;
      set(prev);
      toast.error("Couldn't send the request");
    }
  };

  const base = cn(
    'rounded-full font-semibold flex items-center shrink-0 transition-transform active:scale-[0.97]',
    compact ? 'px-3 h-8 text-xs gap-1.5' : 'px-4 h-11 text-sm gap-2'
  );
  const iconCls = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';

  if (status === 'approved') {
    return (
      <button onClick={openAnalyze} className={cn(base, 'glass-tint text-lap-gain')} data-testid="access-open-analyze">
        <Activity className={iconCls} />
        {compact ? 'Open in Analyze' : 'Access granted — Open in Analyze'}
      </button>
    );
  }
  if (status === 'pending') {
    return (
      <button disabled className={cn(base, 'glass text-lap-dim opacity-60')} data-testid="access-pending">
        <Clock className={iconCls} /> Requested
      </button>
    );
  }
  if (status === 'denied') {
    return (
      <span className="flex items-center gap-2 shrink-0">
        {!compact && <span className="text-[13px] text-lap-dim">Request denied</span>}
        <button onClick={request} className={cn(base, 'glass text-lap-muted')} data-testid="access-rerequest">
          {compact ? 'Denied · Retry' : 'Request again'}
        </button>
      </span>
    );
  }
  return (
    <button onClick={request} className={cn(base, 'glass-tint text-lap-accent-bright')} data-testid="access-request">
      <Lock className={iconCls} /> Request data
    </button>
  );
}

/** 3-column stat row: canyon → Distance/Time/Top speed; else Best lap/Distance/Top speed. */
function StatRow({ post }) {
  const { speedUnit } = useSpeedUnit();
  const s = postStats(post);
  const dist = s.distanceKm != null
    ? `${distanceToDisplay(s.distanceKm, speedUnit).toFixed(1)} ${getDistanceLabel(speedUnit)}`
    : null;
  const speed = s.topSpeedKmh != null ? formatSpeedByUnit(s.topSpeedKmh, speedUnit, 0) : null;
  const time = s.timeS != null ? formatDuration(s.timeS) : null;
  const byLabel = {
    'Best lap': s.bestLapS != null ? formatLapTime(s.bestLapS) : null,
    Distance: dist,
    Time: time,
    'Top speed': speed,
  };
  const cols = statRowLabels(post.kind).map((label) => [label, byLabel[label]]);

  if (cols.every(([, v]) => v == null)) return null;
  return (
    <div className="grid grid-cols-3 gap-3 px-[18px] mt-3">
      {cols.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <div className="text-[11px] font-medium text-lap-dim">{label}</div>
          <div className="num text-lg font-bold text-lap-text truncate">{value ?? '—'}</div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PostCard
// ---------------------------------------------------------------------------

/**
 * Feed card per the iOS spec: author header, title, 3-col stat row,
 * full-width 16:10 route map, Respect/comment/edit/share action row.
 */
export function PostCard({ post, onOpen, onToggleLike, onOpenProfile, onEdit, onAccessChange }) {
  const authorName = post.is_mine
    ? (post.author.username || post.author.name || 'You')
    : (post.author.name || post.author.username);
  const isDrive = post.kind !== 'note';

  return (
    <Card className="overflow-hidden" data-testid={`post-card-${post.id}`}>
      {/* Whole upper region is one tap target → detail */}
      <div className="cursor-pointer" onClick={() => onOpen(post.id)}>
        {/* Author header */}
        <div className="flex items-center gap-3 px-[18px] pt-[18px]">
          <InitialsAvatar
            name={post.author.name || post.author.username}
            avatarUrl={post.author.avatar_url}
            userId={post.author.id}
            isMe={post.is_mine}
            size={38}
            onClick={(e) => {
              e.stopPropagation();
              onOpenProfile(post.author.id);
            }}
          />
          <div className="min-w-0 flex-1">
            <button
              className="block max-w-full truncate text-[15px] font-bold text-lap-text hover:text-lap-accent-bright transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onOpenProfile(post.author.id);
              }}
            >
              {authorName}
              {post.is_mine ? <span className="ml-1.5 text-[11px] font-medium text-lap-dim">You</span> : null}
            </button>
            <div className="text-[11px] font-medium text-lap-dim truncate">{postSubtitle(post)}</div>
          </div>
          {post.visibility === 'premium' && (
            <span className="w-7 h-7 rounded-full bg-white/[0.06] flex items-center justify-center shrink-0" title="Premium">
              <Crown className="w-3.5 h-3.5 text-lap-muted" />
            </span>
          )}
          <KindBadge kind={post.kind} />
        </div>

        {/* Title */}
        <h3 className="px-[18px] mt-3 text-[19px] font-extrabold leading-snug text-lap-text line-clamp-2">
          {formatPostTitle(post.title)}
        </h3>
        {/* iOS cards carry no body; keep a short preview for text-only notes */}
        {!isDrive && post.body && (
          <p className="px-[18px] mt-1.5 text-sm text-lap-muted line-clamp-2">{post.body}</p>
        )}

        {!post.locked && <StatRow post={post} />}

        {/* Full-width media carousel / route map (16:10) */}
        {post.media?.length > 0 ? (
          <MediaCarousel post={post} className="aspect-[16/10] w-full mt-3" />
        ) : (
          isDrive && <PostMap post={post} className="aspect-[16/10] w-full mt-3" />
        )}
      </div>

      {/* Action row */}
      <div className="flex items-center gap-3 px-[18px] py-1.5">
        <RespectButton liked={post.liked_by_me} count={post.like_count} onToggle={() => onToggleLike(post)} />
        <button
          onClick={() => onOpen(post.id)}
          className="flex items-center gap-1.5 p-2.5 -m-1.5 rounded-full text-lap-dim hover:text-lap-muted transition-colors"
          aria-label="View comments"
          data-testid={`post-comments-${post.id}`}
        >
          <MessageCircle className="w-[18px] h-[18px]" />
          <span className={cn('num text-[13px] font-medium', post.comment_count === 0 && 'text-lap-faint')}>
            {post.comment_count || ''}
          </span>
        </button>
        <span className="flex-1" />
        <SessionAccessPill post={post} compact onChange={(sa) => onAccessChange?.(post, sa)} />
        {post.is_mine && onEdit && (
          <button
            onClick={() => onEdit(post)}
            className="p-2.5 -m-1.5 rounded-full text-lap-dim hover:text-lap-muted transition-colors"
            aria-label="Edit post"
            data-testid={`post-edit-${post.id}`}
          >
            <Pencil className="w-[18px] h-[18px]" />
          </button>
        )}
        <button
          onClick={() => sharePost(post)}
          className="p-2.5 -m-1.5 rounded-full text-lap-dim hover:text-lap-muted transition-colors"
          aria-label="Share post"
        >
          <Share2 className="w-[18px] h-[18px]" />
        </button>
      </div>

      {/* Instagram/Strava-style comment preview (latest ≤3 from feed payload) */}
      {(post.comment_count > 0 || (post.preview_comments && post.preview_comments.length > 0)) && (
        <div className="px-[18px] pb-3.5 space-y-1" data-testid={`post-comment-preview-${post.id}`}>
          {post.comment_count > (post.preview_comments?.length || 0) && (
            <button
              type="button"
              onClick={() => onOpen(post.id)}
              className="text-[13px] font-semibold text-lap-dim hover:text-lap-muted transition-colors"
            >
              View all {post.comment_count} comments
            </button>
          )}
          {(post.preview_comments || []).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onOpen(post.id)}
              className="block w-full text-left text-[13px] leading-snug"
            >
              <span className="font-bold text-lap-text">
                {c.author?.name || c.author?.username || 'Driver'}
              </span>{' '}
              <span className="text-lap-muted break-words">{c.body}</span>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}
